import * as React from 'react';
import MuiBox from '@mui/material/Box';
import type { BoxProps as MuiBoxProps } from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SystemProps } from '@mui/system';

const systemPropNames = [
  'alignContent',
  'alignItems',
  'alignSelf',
  'backgroundColor',
  'bgcolor',
  'border',
  'borderBottom',
  'borderColor',
  'borderLeft',
  'borderRadius',
  'borderRight',
  'borderTop',
  'bottom',
  'boxShadow',
  'boxSizing',
  'color',
  'columnGap',
  'display',
  'displayPrint',
  'flex',
  'flexBasis',
  'flexDirection',
  'flexGrow',
  'flexShrink',
  'flexWrap',
  'fontFamily',
  'fontSize',
  'fontStyle',
  'fontWeight',
  'gap',
  'gridArea',
  'gridAutoColumns',
  'gridAutoFlow',
  'gridAutoRows',
  'gridColumn',
  'gridRow',
  'gridTemplateAreas',
  'gridTemplateColumns',
  'gridTemplateRows',
  'height',
  'justifyContent',
  'justifyItems',
  'justifySelf',
  'left',
  'letterSpacing',
  'lineHeight',
  'm',
  'margin',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'marginTop',
  'marginX',
  'marginY',
  'maxHeight',
  'maxWidth',
  'mb',
  'minHeight',
  'minWidth',
  'ml',
  'mr',
  'mt',
  'mx',
  'my',
  'order',
  'overflow',
  'p',
  'padding',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
  'paddingX',
  'paddingY',
  'pb',
  'pl',
  'position',
  'pr',
  'pt',
  'px',
  'py',
  'right',
  'rowGap',
  'textAlign',
  'textOverflow',
  'top',
  'typography',
  'visibility',
  'whiteSpace',
  'width',
  'zIndex',
] as const;

const systemPropNameSet = new Set<string>(systemPropNames);

type CompatBoxExtraProps = {
  alt?: string;
  href?: string;
  rel?: string;
  src?: string;
  target?: string;
  to?: string;
  type?: React.ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

type LegacyBoxSystemProps = SystemProps<Theme>;

export type MuiBoxCompatProps = MuiBoxProps<'div', CompatBoxExtraProps> &
  LegacyBoxSystemProps & {
    onClick?: React.MouseEventHandler<HTMLElement>;
  };

const mergeSx = (
  systemSx: Record<string, unknown>,
  sx: SxProps<Theme> | undefined
): SxProps<Theme> | undefined => {
  if (Object.keys(systemSx).length === 0) {
    return sx;
  }

  if (sx === undefined) {
    return systemSx;
  }

  return (
    Array.isArray(sx) ? [systemSx, ...sx] : [systemSx, sx]
  ) as SxProps<Theme>;
};

const MuiBoxCompat = React.forwardRef<unknown, MuiBoxCompatProps>(
  function MuiBoxCompat(props, ref) {
    const { sx, ...restProps } = props;
    const forwardedProps = { ...restProps } as Record<string, unknown>;
    const systemSx: Record<string, unknown> = {};

    Object.keys(forwardedProps).forEach((propName) => {
      if (systemPropNameSet.has(propName)) {
        systemSx[propName] = forwardedProps[propName];
        delete forwardedProps[propName];
      }
    });

    return (
      <MuiBox
        {...(forwardedProps as MuiBoxProps)}
        ref={ref}
        sx={mergeSx(systemSx, sx as SxProps<Theme> | undefined)}
      />
    );
  }
);

export default MuiBoxCompat;
